import { useLazyQuery } from '@apollo/client';
import { map } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import {
  BREAKPOINTS,
  DATE_ISO_FORMAT,
  DEFAULT_PAGE_SIZE,
  DEFAULTDATEFORMAT,
  REGISTER_STATUS_LABEL,
  ROUTES,
  TAB_KEYS,
} from '../../../../../../../common/constants';
import useRouter from '../../../../../../../common/useRouter';
import { titleCase, toCamelCase } from '../../../../../../../common/utils';
import CommonCard from '../../../../../../../components/CommonCard';
import CommonTable from '../../../../../../../components/CommonTable';
import InfiniteScrollHandler from '../../../../../../../components/InfiniteScrollHandler';
import { GET_REGISTER_LOGS } from '../../../../../graphql/Queries';

const SetsTable = ({
  registerDetailsData,
  registerId,
  showListView,
  setIsDataEmpty,
  fromDate,
  toDate,
}) => {
  const {
    navigate,
    params: { projectId },
    location,
  } = useRouter();
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const initialLogsFilter = {
    skip: 0,
    limit: DEFAULT_PAGE_SIZE,
    fromDate,
    toDate,
    status: REGISTER_STATUS_LABEL.LIVE,
  };
  const initialPaginationValue = {
    total: 0,
    current: 1,
  };
  const [setsData, setSetsData] = useState([]);
  const [scrollFlag, setScrollFlag] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [registerLogsFilter, setRegisterLogsFilter] =
    useState(initialLogsFilter);
  const [paginationProps, setPaginationProps] = useState(
    initialPaginationValue,
  );
  const registerNavFilter = location?.state?.registerNavFilter;

  const [getRegisterLogs, { loading }] = useLazyQuery(GET_REGISTER_LOGS, {
    fetchPolicy: 'cache-and-network',
    onCompleted(response) {
      const data = response?.registerLogDetailEntryByRegisterList?.data;
      const total = response?.registerLogDetailEntryByRegisterList?.total;
      const pagination = {
        ...paginationProps,
        total,
      };
      setIsDataEmpty(!(total > 0));
      const result = map(data, (item, index) => {
        const obj = Object.keys(item)?.reduce((acc, curr) => {
          const newKey =
            curr === 'createdAt' || curr === 'createdBy'
              ? curr
              : toCamelCase(curr);
          if (curr === 'createdAt' && item[curr]) {
            acc[newKey] = moment(item[curr])?.format(DEFAULTDATEFORMAT);
          } else {
            acc[newKey] = item[curr];
          }
          return acc;
        }, {});
        obj.index = registerLogsFilter.skip + index + 1;
        return obj;
      });

      if (scrollFlag) {
        setSetsData((prevData) => [...prevData, ...result]);
        setScrollFlag(false);
      } else {
        setSetsData(result);
      }
      setHasMore(setsData.length + result.length < total);
      setPaginationProps(pagination);
    },
    onError() {},
  });

  useEffect(() => {
    setRegisterLogsFilter({
      ...registerLogsFilter,
      fromDate: fromDate || null,
      toDate: toDate || null,
    });
  }, [fromDate, toDate]);

  useEffect(() => {
    if (!registerDetailsData?.id) return;
    if (registerDetailsData !== undefined) {
      getRegisterLogs({
        variables: {
          registerId: registerDetailsData?.id,
          filter: registerLogsFilter,
        },
      });
    }
  }, [registerDetailsData, registerLogsFilter]);

  const columns = [
    {
      title: '#',
      key: 'id',
      dataIndex: 'index',
      render: (text, record, index) => (
        <div>{registerLogsFilter?.skip + index + 1}</div>
      ),
    },
    ...map(registerDetailsData?.registerDetails, (data) => ({
      title: data?.isRequired ? (
        <div className="d-flex">
          <div className="danger-text mr-5">*</div>
          <div className="mr-5">{data?.name}</div>
        </div>
      ) : (
        `${data?.name}`
      ),
      isRequired: `${data?.isRequired}`,
      dataIndex: `${toCamelCase(data?.name)}`,
      index: `${toCamelCase(data?.name)}`,
      render: (text) => {
        if (moment(text, DATE_ISO_FORMAT, true).isValid()) {
          return moment(text)?.format(DEFAULTDATEFORMAT);
        }
        return <div>{text || '-'}</div>;
      },
    })),
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      index: 'createdBy',
    },
    {
      title: 'Created On',
      dataIndex: 'createdAt',
      index: 'createdAt',
    },
  ];

  // eslint-disable-next-line no-unused-vars
  const handleTableChange = (pagination, paginationFilter) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setPaginationProps({ ...paginationProps, ...pagination });
    setRegisterLogsFilter({
      ...registerLogsFilter,
      skip,
      limit: pagination?.pageSize,
    });
    getRegisterLogs({
      variables: {
        registerId: registerDetailsData?.id,
        filter: {
          ...registerLogsFilter,
          skip,
          limit: pagination.pageSize,
        },
      },
    });
  };

  const handleRowClick = (record) => {
    const registerLogId = record?.id;
    navigate(
      `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/${registerLogId}`,
      {
        state: {
          registerDetailsData,
          currentPath: location?.pathname,
          showListView,
          registerNavFilter,
          registerId,
        },
      },
    );
  };

  const handleRefetch = () => {
    if (!loading && hasMore) {
      setScrollFlag(true);
      getRegisterLogs({
        variables: {
          registerId: registerDetailsData?.id,
          filter: {
            ...registerLogsFilter,
            skip: setsData?.length,
            limit: DEFAULT_PAGE_SIZE,
          },
        },
      });
    }
  };

  return (
    <div className="pointer">
      {isDesktopViewport ? (
        <CommonTable
          data={setsData || []}
          columns={columns}
          loading={loading}
          onChange={handleTableChange}
          paginationConfig={paginationProps}
          onRow={(record) => {
            return {
              onClick: () => handleRowClick(record),
            };
          }}
        />
      ) : (
        <InfiniteScrollHandler
          scrollFlag={scrollFlag}
          setScrollFlag={setScrollFlag}
          hasMore={hasMore}
          loading={loading}
          refetchData={handleRefetch}
          dataLength={setsData?.length}
          skeletonRows={columns?.length - 2}
        >
          <div>
            {map(setsData, (data, index) => {
              return (
                <CommonCard key={index} onClick={() => handleRowClick(data)}>
                  <div className="common-card d-flex">
                    <div className="mr-5 fw-medium">{index + 1}.</div>
                    <div>
                      <div className="card-content text-secondary">
                        <br />
                        {Object.entries(data)
                          ?.filter(([key]) => key !== 'index' && key !== 'id')
                          ?.map(([key, value]) => {
                            const fieldDetail =
                              registerDetailsData?.registerDetails?.find(
                                (detail) => toCamelCase(detail?.name) === key,
                              );
                            return (
                              <div className="mb-15" key={key}>
                                <span className="fw-medium mr-5">
                                  {fieldDetail?.isRequired && (
                                    <span className="danger-text mr-5">*</span>
                                  )}
                                  {key === 'createdAt'
                                    ? 'Created On'
                                    : titleCase(key)}
                                  :
                                </span>
                                <span>{value !== null ? value : '-'}</span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </CommonCard>
              );
            })}
          </div>
        </InfiniteScrollHandler>
      )}
    </div>
  );
};

export default SetsTable;
